/**
 * Offline Storage Manager
 * Handles downloading and storing content for offline access using IndexedDB
 */

const DB_NAME = 'SmarterLearningHub';
const DB_VERSION = 1;
const STORE_NAME = 'downloads';

class OfflineStorageManager {
  constructor() {
    this.db = null;
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          objectStore.createIndex('type', 'type', { unique: false });
          objectStore.createIndex('seriesId', 'seriesId', { unique: false });
          objectStore.createIndex('downloadedAt', 'downloadedAt', { unique: false });
        }
      };
    });
  }

  async downloadFile(url, progressCallback) {
    const response = await fetch(url);
    const reader = response.body.getReader();
    const contentLength = +response.headers.get('Content-Length');

    let receivedLength = 0;
    let chunks = [];

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      chunks.push(value);
      receivedLength += value.length;

      if (progressCallback) {
        const progress = Math.round((receivedLength / contentLength) * 100);
        progressCallback(progress);
      }
    }

    const blob = new Blob(chunks);
    return blob;
  }

  async saveContent(content) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(content);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getContent(id) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllDownloads() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getSeriesDownloads(seriesId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('seriesId');
      const request = index.getAll(seriesId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteContent(id) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async deleteSeriesDownloads(seriesId) {
    if (!this.db) await this.init();

    const downloads = await this.getSeriesDownloads(seriesId);
    const deletePromises = downloads.map(d => this.deleteContent(d.id));
    return Promise.all(deletePromises);
  }

  async getStorageUsage() {
    if (!this.db) await this.init();

    const downloads = await this.getAllDownloads();
    let totalSize = 0;

    downloads.forEach(download => {
      if (download.blob) {
        totalSize += download.blob.size;
      }
    });

    return {
      totalSize,
      totalFiles: downloads.length,
      downloads
    };
  }

  formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
}

export const offlineStorage = new OfflineStorageManager();

// Download helpers
export async function downloadEpisode(seriesId, episode, progressCallback) {
  const content = {
    id: `episode_${episode.episode_id}`,
    type: 'episode',
    seriesId: seriesId,
    episodeId: episode.episode_id,
    episodeNumber: episode.episode_number,
    title: episode.title,
    filename: episode.filename,
    downloadedAt: new Date().toISOString()
  };

  // Download the actual file
  const blob = await offlineStorage.downloadFile(
    episode.file_url || `/uploads/cinematic/${seriesId}/${episode.episode_id}${episode.file_extension}`,
    progressCallback
  );

  content.blob = blob;
  content.blobUrl = URL.createObjectURL(blob);
  
  await offlineStorage.saveContent(content);
  return content;
}

export async function downloadChapter(seriesId, chapter, progressCallback) {
  const content = {
    id: `chapter_${chapter.chapter_id}`,
    type: 'chapter',
    seriesId: seriesId,
    chapterId: chapter.chapter_id,
    chapterNumber: chapter.chapter_number,
    title: chapter.title,
    filename: chapter.filename,
    downloadedAt: new Date().toISOString()
  };

  // Download the actual file
  const blob = await offlineStorage.downloadFile(
    chapter.file_url || `/uploads/audiobooks/${seriesId}/${chapter.chapter_id}${chapter.file_extension}`,
    progressCallback
  );

  content.blob = blob;
  content.blobUrl = URL.createObjectURL(blob);
  
  await offlineStorage.saveContent(content);
  return content;
}

export async function downloadSingleAudio(contentId, audioData, progressCallback) {
  const content = {
    id: `audio_${contentId}`,
    type: 'single_audio',
    contentId: contentId,
    title: audioData.title,
    filename: audioData.filename,
    downloadedAt: new Date().toISOString()
  };

  const blob = await offlineStorage.downloadFile(
    audioData.file_url || audioData.url,
    progressCallback
  );

  content.blob = blob;
  content.blobUrl = URL.createObjectURL(blob);
  
  await offlineStorage.saveContent(content);
  return content;
}

export async function isContentDownloaded(id) {
  const content = await offlineStorage.getContent(id);
  return !!content;
}

export async function getDownloadedContent(id) {
  return await offlineStorage.getContent(id);
}

export async function deleteDownload(id) {
  return await offlineStorage.deleteContent(id);
}

export async function getAllDownloads() {
  return await offlineStorage.getAllDownloads();
}

/**
 * Reconcile IndexedDB downloads against a set of currently-valid content IDs
 * from the server. Any local download whose source has been deleted on the
 * backend is purged here so the Offline Library stops showing ghosts.
 *
 * `validIds` should be a Set containing strings like:
 *   - `episode_${episode_id}` for cinematic
 *   - `chapter_${chapter_id}` for audiobook chapters
 *   - `audio_${content_id}`   for standalone audiobooks
 */
export async function reconcileDownloads(validIds) {
  try {
    const all = await offlineStorage.getAllDownloads();
    const orphans = all.filter((d) => d && d.id && !validIds.has(d.id));
    await Promise.all(orphans.map((d) => offlineStorage.deleteContent(d.id)));
    return {
      checked: all.length,
      purged: orphans.length,
      purgedIds: orphans.map((d) => d.id),
    };
  } catch (err) {
    // IndexedDB is best-effort; never block the library UI.
    console.warn('[offlineStorage] reconcileDownloads failed:', err);
    return { checked: 0, purged: 0, purgedIds: [], error: String(err) };
  }
}

export default offlineStorage;
